import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import {
  IconButton,
  Box,
  Tooltip,
  Snackbar,
  Typography,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  TextField,
} from "@mui/material";
import { LinkedIn, ContentCopy, Email, X, Close } from "@mui/icons-material";
import { shareContentAnalytics } from "../../../../store/api/analytics";
import { useOrganisation } from "../../../../hooks/useOrganisation";
import { IMAGE_URL_AZURE } from "../../../../constants/env";
import Button from "../../../common/Button";

const ShareHeader = ({ article }) => {
  const { companyName, logoLink, primaryColor } = useOrganisation();
  const [copySuccess, setCopySuccess] = useState(false);
  const [sharePreviewOpen, setSharePreviewOpen] = useState(false);
  const [shareType, setShareType] = useState("");
  const [customMessage, setCustomMessage] = useState("");
  const { id } = useParams();

  const [shareMetadata, setShareMetadata] = useState({
    title: "",
    summary: "",
    source: "",
    logoUrl: "",
  });

  const { mutate: shareMutate } = useMutation({
    mutationFn: () => shareContentAnalytics({ contentId: id }),
  });

  useEffect(() => {
    if (companyName) {
      const fullLogoUrl = logoLink
        ? new URL(logoLink, window.location.origin).toString()
        : "";

      setShareMetadata({
        title: `${companyName} - ${article?.title}`,
        summary: article?.text
          ? article.text.substring(0, 200).replace(/<[^>]*>?/gm, "") + "..."
          : "Interesting article to share",
        source: window.location.hostname,
        logoUrl: fullLogoUrl,
      });
    }
  }, [companyName, logoLink, article]);

  // Function to open popup window
  const openPopupWindow = (url, title) => {
    const width = 600;
    const height = 600;
    const left = window.innerWidth / 2 - width / 2;
    const top = window.innerHeight / 2 - height / 2;

    const popup = window.open(
      url,
      title,
      `width=${width},height=${height},left=${left},top=${top},toolbar=0,location=0,menubar=0,directories=0,scrollbars=1`
    );

    if (!popup || popup.closed || typeof popup.closed === "undefined") {
      window.open(url, "_blank");
    } else {
      popup.focus();
    }
  };

  const handleShareButtonClick = (type) => {
    setShareType(type);
    setSharePreviewOpen(true);
    setCustomMessage(
      type === "LinkedIn"
        ? `Check out this article from ${companyName}`
        : `${shareMetadata.title} - ${window.location.href}`
    );
  };

  const handleCloseSharePreview = () => {
    setSharePreviewOpen(false);
  };

  const handleShareNow = () => {
    const articleUrl = window.location.href;

    let shareUrl;
    if (shareType === "LinkedIn") {
      const params = {
        mini: "true",
        url: articleUrl,
        title: shareMetadata.title,
        summary: customMessage || shareMetadata.summary,
        source: shareMetadata.source,
      };
      const queryString = Object.entries(params)
        .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
        .join("&");
      shareUrl = `https://www.linkedin.com/sharing/share-offsite/?${queryString}`;
    } else if (shareType === "Twitter") {
      const tweetText = encodeURIComponent(
        customMessage || shareMetadata.title
      );
      const tweetUrl = encodeURIComponent(articleUrl);
      shareUrl = `https://twitter.com/intent/tweet?text=${tweetText}&url=${tweetUrl}`;
    } else if (shareType === "Email") {
      const subject = encodeURIComponent(shareMetadata.title);
      const body = encodeURIComponent(
        customMessage || `I found this interesting article: ${articleUrl}`
      );
      shareUrl = `mailto:?subject=${subject}&body=${body}`;
      window.location.href = shareUrl;
      shareMutate(id);
      setSharePreviewOpen(false);
      return;
    }

    openPopupWindow(shareUrl, `Share on ${shareType}`);
    shareMutate(id);
    setSharePreviewOpen(false);
  };

  const handleCopyLink = () => {
    const articleUrl = window.location.href;
    navigator.clipboard.writeText(articleUrl).then(() => {
      setCopySuccess(true);
    });
    shareMutate(id);
  };

  const handleCloseSnackbar = () => {
    setCopySuccess(false);
  };

  return (
    <>
      <Box display="flex" alignItems="center" padding={2}>
        <Tooltip title="Share on LinkedIn">
          <IconButton
            onClick={() => handleShareButtonClick("LinkedIn")}
            aria-label="Share on LinkedIn"
          >
            <LinkedIn style={{ color: "#2677b5" }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Share on Twitter">
          <IconButton
            onClick={() => handleShareButtonClick("Twitter")}
            aria-label="Share on Twitter (X)"
          >
            <X />
          </IconButton>
        </Tooltip>
        <Tooltip title="Share via Email">
          <IconButton
            onClick={() => handleShareButtonClick("Email")}
            aria-label="Share via Email"
          >
            <Email />
          </IconButton>
        </Tooltip>
        <Tooltip title="Copy link">
          <IconButton onClick={handleCopyLink}>
            <ContentCopy />
          </IconButton>
        </Tooltip>
        <Snackbar
          open={copySuccess}
          autoHideDuration={2000}
          onClose={handleCloseSnackbar}
          message="Link copied!"
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        />
      </Box>

      {/* Custom Share Preview Dialog */}
      <Dialog
        open={sharePreviewOpen}
        onClose={handleCloseSharePreview}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Share on {shareType}
          <IconButton
            aria-label="close"
            onClick={handleCloseSharePreview}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Customize your message
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              variant="outlined"
              value={customMessage}
              onChange={(e) => setCustomMessage(e.target.value)}
              placeholder={`Share your thoughts about this article...`}
            />
          </Box>

          <Box
            sx={{
              mb: 3,
              p: 2,
              bgcolor: "background.paper",
              borderRadius: 1,
              boxShadow: 1,
            }}
          >
            <Box sx={{ display: "flex", mb: 2 }}>
              {article?.mainImage && (
                <Box
                  sx={{
                    mr: 2,
                    width: 80,
                    height: 80,
                    overflow: "hidden",
                    borderRadius: 1,
                  }}
                >
                  <img
                    src={`${article.mainImage}?${IMAGE_URL_AZURE}`}
                    alt={article.title}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                </Box>
              )}
              <Box>
                <Typography variant="subtitle1" fontWeight="bold">
                  {shareMetadata.title}
                </Typography>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ mt: 1 }}
                >
                  {shareMetadata.summary}
                </Typography>
              </Box>
            </Box>
            <Typography variant="caption" color="textSecondary">
              {window.location.href}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseSharePreview}
            text="Cancel"
            backgroundColor="white"
          />
          <Button
            onClick={handleShareNow}
            text={`Share on ${shareType}`}
            backgroundColor={primaryColor}
          />
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ShareHeader;
